import React, { useEffect, useState } from 'react';
import { InputComponent } from '@nebulr-group/nblocks-react/lib/components/shared/InputComponent';
import {
  OptionsComponent,
  Selectable,
} from '../shared/options/options.component';
import {
  KeyTarget,
  OrgTarget,
  Target,
  TargetValue,
  UserTarget,
} from 'src/gql/graphql';
import { ApolloUtils } from 'src/shared/apollo-utils';
import { SafeTenantName } from '../tenant/safe-tenant-name/safe-tenant-name.component';
import { useListTenants } from 'src/shared/hooks/use-list-tenants';
import { useListCurrentPlans } from 'src/shared/hooks/use-list-plans';
import { useListCurrentRoles } from 'src/shared/hooks/use-list-roles';

export interface ComponentProps {
  target: Target;
  onChange: (target: Target) => void;
}

const fields: Selectable[] = [
  { value: 'org-plan', label: 'Workspace plan' },
  { value: 'org-key', label: 'Workspace key' },
  { value: 'org-id', label: 'Workspace ID' },
  { value: 'org-name', label: 'Workspace name' },
  { value: 'user-key', label: 'User key' },
  // { id: 'user-id', label: 'User ID' },
  { value: 'user-name', label: 'User name' },
  { value: 'user-role', label: 'User role' },
  { value: 'device-key', label: 'Device key' },
];

const operators: Selectable[] = [
  { value: 'eq', label: 'Equals' },
  { value: 'beginsWith', label: 'Begins with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'contains', label: 'Contains' },
];

type OperatorType = 'eq' | 'beginsWith' | 'endsWith' | 'contains';

type ReversedTarget = { field: string; operator: string; value: string };

const reverseTarget = (target: Target): ReversedTarget | undefined => {
  const cleanTarget = ApolloUtils.removeTypeName<any>(target);
  const contextType = Object.keys(cleanTarget).filter(
    (key) => !!(target as any)[key],
  )[0];
  switch (contextType) {
    case 'user':
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return reverseTargetType(cleanTarget.user!, 'user');
    case 'org':
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return reverseTargetType(cleanTarget.org!, 'org');
    case 'device':
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return reverseTargetType(cleanTarget.device!, 'device');
    default:
      break;
  }
};

const reverseTargetType = (
  target: UserTarget | OrgTarget | KeyTarget,
  prefix: string,
): { field: string; operator: string; value: string } => {
  const field = Object.keys(target).filter((key) => !!(target as any)[key])[0];

  if (field === 'id') {
    return {
      field: `${prefix}-${field}`,
      operator: 'eq',
      value: (target as any).id,
    };
  } else {
    const targetValue: TargetValue = (target as any)[field];
    return {
      field: `${prefix}-${field}`,
      operator: targetValue.operator,
      value: targetValue.value,
    };
  }
};

const SegmentTargetComponent = ({ target, onChange }: ComponentProps) => {
  const [field, setField] = useState<Selectable | undefined>(fields[0]);
  const [operator, setOperator] = useState<OperatorType>('eq');
  const [value, setValue] = useState('');

  const { data: tenantsQuery } = useListTenants();
  const { data: plansQuery } = useListCurrentPlans();
  const { data: rolesQuery } = useListCurrentRoles();

  const nblocksTenants: Selectable[] = tenantsQuery
    ? tenantsQuery.listTenants.map((t) => {
        return { value: t.id, label: `${t.id} (${SafeTenantName(t.name)})` };
      })
    : [];

  const nblocksPlans: Selectable[] = plansQuery
    ? plansQuery.getAppPlans.map((p) => {
        return { value: p.key, label: `${p.key} (${p.name})` };
      })
    : [];

  const nblocksRoles: Selectable[] = rolesQuery
    ? rolesQuery.listRoles.map((r) => {
        return { value: r.key, label: `${r.key} (${r.name})` };
      })
    : [];

  useEffect(() => {
    const tReverse = reverseTarget(target);
    if (tReverse) {
      // Edit target
      const _field = fields.find((f) => f.value === tReverse.field);
      setField(_field);
      setOperator(tReverse.operator as OperatorType);
      setValue(tReverse.value);
    } else {
      // New target
      if (field) handleValueEffectOfField(field?.value);
    }
  }, [tenantsQuery, plansQuery, rolesQuery]);

  const generateTarget = (): Target => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    switch (field!.value) {
      case 'user-key':
        return { user: { key: { operator, value } } };
      case 'user-name':
        return { user: { name: { operator, value } } };
      case 'user-role':
        return { user: { role: { operator, value } } };
      case 'org-key':
        return { org: { key: { operator, value } } };
      case 'org-id':
        return { org: { id: value } };
      case 'org-plan':
        return { org: { plan: { operator, value } } };
      case 'org-name':
        return { org: { name: { operator, value } } };
      case 'device-key':
        return { device: { key: { operator, value } } };
      default:
        return {};
    }
  };

  const formValid = field && operator && value;

  useEffect(() => {
    if (formValid) {
      onChange(generateTarget());
    }
  }, [field, operator, value]);

  const onDidSelectField = (selectable: Selectable) => {
    setField(selectable);
    handleValueEffectOfField(selectable.value);
  };

  const handleValueEffectOfField = (fieldValue: string) => {
    // Reset value if we're moving to a "magic" field
    switch (fieldValue) {
      case 'org-id':
        setOperator('eq');
        setValue(nblocksTenants.length > 0 ? nblocksTenants[0].value : '');
        break;
      case 'org-plan':
        setValue(nblocksPlans.length > 0 ? nblocksPlans[0].value : '');
        break;
      case 'user-role':
        setValue(nblocksRoles.length > 0 ? nblocksRoles[0].value : '');
        break;

      default:
        setValue('');
        break;
    }
  };

  const onDidSelectOperator = (selectable: Selectable) => {
    setOperator(
      selectable.value as 'eq' | 'beginsWith' | 'endsWith' | 'contains',
    );
  };

  const renderOperatorInput = () => {
    switch (field?.value) {
      case 'org-id':
        return (
          <OptionsComponent
            label="Operator"
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            data={[operators.find((o) => o.value === operator)!]}
            init={operators.find((o) => o.value === operator)}
            didSelect={(selectable) => onDidSelectOperator(selectable)}
          />
        );

      default:
        return (
          <OptionsComponent
            label="Operator"
            data={operators}
            init={operators.find((o) => o.value === operator)}
            didSelect={(selectable) => onDidSelectOperator(selectable)}
          />
        );
    }
  };

  const renderValueInput = () => {
    switch (field?.value) {
      case 'org-id':
        return (
          <OptionsComponent
            label="Nblocks workspaces"
            search={nblocksTenants.length > 5}
            data={nblocksTenants}
            init={nblocksTenants.find((t) => t.value === value)}
            didSelect={(selectable) => setValue(selectable.value)}
          />
        );

      case 'org-plan':
        return (
          <OptionsComponent
            label="Nblocks plans"
            data={nblocksPlans}
            init={nblocksPlans.find((t) => t.value === value)}
            didSelect={(selectable) => setValue(selectable.value)}
          />
        );

      case 'user-role':
        return (
          <OptionsComponent
            label="Nblocks roles"
            data={nblocksRoles}
            init={nblocksRoles.find((t) => t.value === value)}
            didSelect={(selectable) => setValue(selectable.value)}
          />
        );

      default:
        return (
          <InputComponent
            type="text"
            label="Value*"
            placeholder="E.g. iphone or john@doe.com"
            name="id"
            onChange={(event) => setValue(event.target.value)}
            value={value}
          />
        );
    }
  };

  return (
    <>
      <div>
        <OptionsComponent
          label="Field"
          data={fields}
          init={field}
          didSelect={(selectable) => onDidSelectField(selectable)}
        />
      </div>
      <div>{renderOperatorInput()}</div>
      <div>{renderValueInput()}</div>
      <hr />
    </>
  );
};

export type { ReversedTarget };
export { SegmentTargetComponent, reverseTarget, operators, fields };
